import React, {type CSSProperties} from 'react';

import {RootMachineContext} from '../../machines/rootMachine';
import {ReasonForm} from '../reason-form/reason-form';
import {Header} from '../header/header';
import {ReasonSuccess} from '../reason-success/reason-success';
import {CancellationPolicy} from '../cancellation-policy/cancellation-policy';
import {RsvpFlow} from '../rsvp-flow/rsvp-flow';
import {IdentificationFlow} from '../identification-flow/identification-flow';
import {ErrorPage} from '../error-page/error-page';
import {RescheduleLimitReached} from '../reschedule-limit-reached/reschedule-limit-reached.tsx';

// booking flow
import {BookingLayout} from '../booking-layout/booking-layout';
import {NotifySuccess} from '../notify-success/notify-success.tsx';
import {BookingSuccess} from '../booking-success/booking-success.tsx';

// selectors
import {selectBrandSettings} from '../../selectors/brandSelectors';

// session flow
import {SessionExpired} from '../session/session-expired';

// atomic components
import {TimezonePicker} from '../timezone-picker/timezone-picker';
import {UnsupportedBrowser} from '../unsupported-browser/unsupported-browser';

import {CircularLoader} from '@spaced-out/ui-design-system';

import css from './root-layout.css';


export const RootLayout = () => {
  const state = RootMachineContext.useSelector((state) => state);
  const brandSettings = RootMachineContext.useSelector(selectBrandSettings);

  const eventLoading = state.matches('eventLoading');
  const sessionLoading = state.matches('sessionLoading');
  const eventTemplateLoading = state.matches('eventTemplateLoading');
  const isLoading = eventLoading || sessionLoading || eventTemplateLoading;

  const eventError = state.matches('eventLoadingError');
  const sessionError = state.matches('sessionError');
  const templateError = state.matches('eventTemplateError');
  const isError = eventError || sessionError || templateError;

  if (isLoading) {
    return <CircularLoader className={css.loader} size="large" />;
  }

  if (isError) {
    return <ErrorPage />;
  }

  const bookingSuccessPage =
    state.matches('attendeeGenerated') ||
    state.matches('bookingCancelForm') ||
    state.matches('bookingCancelSubmitting') ||
    state.matches('bookingCancelError');

  return (
    <div
      className={css.layout}
      style={
        {
          '--brand-color': brandSettings?.color,
          '--brand-button-color': brandSettings?.button_color,
        } as CSSProperties
      }
    >
      {brandSettings && <Header />}

      <div className={css.content}>
        {/* Browser not supported */}
        {state.matches('unsupportedBrowser') && <UnsupportedBrowser />}

        {/* Timezone picker */}
        {state.matches('timezonePicker') && <TimezonePicker />}

        {/* cancellation flow starts */}
        {state.matches({cancellationFlow: 'reasonForm'}) && <ReasonForm />}
        {state.matches({cancellationFlow: 'cancellationPolicy'}) && (
          <CancellationPolicy />
        )}
        {state.matches('cancelSuccess') && <ReasonSuccess />}
        {state.matches({cancellationFlow: 'identificationFlow'}) && (
          <IdentificationFlow />
        )}
        {/* cancellation flow ends */}

        {/* reschedule flow starts */}
        {state.matches({rescheduleFlow: 'rescheduleLimitReached'}) && (
          <RescheduleLimitReached />
        )}
        {state.matches({rescheduleFlow: 'bookingFlow'}) && <BookingLayout />}
        {state.matches({rescheduleFlow: 'reasonForm'}) && <ReasonForm />}
        {state.matches('RequestRescheduleSuccess') && <ReasonSuccess />}
        {state.matches({rescheduleFlow: 'identificationFlow'}) && (
          <IdentificationFlow />
        )}
        {/* reschedule flow ends */}

        {/* booking flow starts */}
        {state.matches('bookingFlow') && <BookingLayout />}
        {bookingSuccessPage && <BookingSuccess />}
        {/* booking flow ends */}

        {/* session flow starts */}
        {state.matches('sessionExpired') && <SessionExpired />}
        {/* session flow ends */}

        {state.matches('notifySuccess') && <NotifySuccess />}
        {state.matches('rsvpFlow') && <RsvpFlow />}
      </div>
    </div>
  );
};
